import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import useScrollEnter from '../../hooks/useScrollEnter';
import useSourceFromQuery from '../../hooks/useSourceFromQuery';
import { useSubscription } from '../../hooks/useSubscription';

import { useIsCheckingAuth } from '../../contexts/AuthContext';
import { useBrand, useDefaultBrand, useSite } from '../../contexts/SiteContext';
import UrbaniaButton from '../buttons/UrbaniaButton';
import SubscriptionForm from '../forms/SubscriptionForm';
import PromotionDescription from '../typography/PromotionDescription';
import PromotionTitle from '../typography/PromotionTitle';

import styles from '../../styles/pages/subscription-page.module.css';

const propTypes = {
    brandSlug: PropTypes.string,
    disabled: PropTypes.bool,
    current: PropTypes.bool,
    subscribe: PropTypes.bool,
    entered: PropTypes.bool,
    entering: PropTypes.bool,
    leaving: PropTypes.bool,
    onEnter: PropTypes.func,
    onLeave: PropTypes.func,
    className: PropTypes.string,
    contentClassName: PropTypes.string,
};

const defaultProps = {
    brandSlug: null,
    disabled: false,
    current: false,
    subscribe: false,
    entered: false,
    entering: false,
    leaving: false,
    onEnter: null,
    onLeave: null,
    className: null,
    contentClassName: null,
};
function SubscriptionPage({
    brandSlug,
    disabled,
    subscribe: shouldSubscribe,
    current,
    entered,
    entering,
    leaving,
    onEnter,
    onLeave,
    className,
    contentClassName,
}) {
    const hasBrand = brandSlug !== null;
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();
    const brand = useBrand(brandSlug, !hasBrand ? defaultBrandHandle : null);
    const {
        handle: brandHandle = null,
        label: brandLabel,
        default: isDefault = false,
    } = brand || {};
    const sourceQuery = useSourceFromQuery();
    const { subscriptions } = useSite();
    const subscription = subscriptions.find(
        ({ handle: subscriptionHandle }) => subscriptionHandle === brandHandle,
    );
    const isCheckingAuth = useIsCheckingAuth();
    const user = useUser();
    const { description } = subscription;
    const { subscribed, subscribe } = useSubscription({
        subscription: brandHandle,
        source: sourceQuery || 'link',
    });

    useScrollEnter({
        disabled: !current,
        onEnter,
        onLeave,
    });

    useEffect(() => {
        if (shouldSubscribe && current) {
            subscribe();
        }
    }, [shouldSubscribe]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.entering]: entering,
                    [styles.leaving]: leaving,
                },
                className,
                contentClassName,
            ])}
            data-theme="dark"
            data-brand={brandHandle}
        >
            <UrbaniaButton brand={brandHandle} className={styles.urbania} />
            <div className={styles.wrapper}>
                <div className={styles.inner}>
                    <PromotionTitle className={styles.title}>
                        {subscribed ? (
                            <FormattedMessage
                                defaultMessage="Vous êtes abonné·e à {brand}!"
                                description="Page title"
                                values={{
                                    brand: brandLabel,
                                }}
                            />
                        ) : brandHandle === 'urbania' ? (
                            <FormattedMessage
                                defaultMessage="Abonnez-vous, c’est gratuit!"
                                description="Page title"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Abonnez-vous à {brand}!"
                                description="Page title"
                                values={{
                                    brand: brandLabel,
                                }}
                            />
                        )}
                    </PromotionTitle>
                    {brandHandle === 'urbania' && !subscribed ? (
                        <PromotionDescription className={styles.description}>
                            Entrez votre courriel ou votre numéro de téléphone pour accéder à votre
                            profil, profiter de rabais exclusifs chez nos partenaires, consulter
                            votre historique et gérer vos abonnements.
                        </PromotionDescription>
                    ) : (
                        <PromotionDescription className={styles.description} html={description} />
                    )}
                    <div className={styles.form}>
                        {!isCheckingAuth || user !== null ? (
                            <SubscriptionForm
                                subscription={brandHandle}
                                via={
                                    brandHandle === 'urbania' || brandHandle === 'france'
                                        ? ['sms', 'email']
                                        : null
                                }
                                full
                                source={sourceQuery || 'page'}
                                ssoSize="large"
                                withoutButtons
                                className={styles.form}
                                fieldsClassName={styles.fields}
                                subscriptionsClassName={styles.subscriptions}
                                ssoClassName={styles.sso}
                            />
                        ) : (
                            <div className={styles.placeholder}>Chargement...</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

SubscriptionPage.propTypes = propTypes;
SubscriptionPage.defaultProps = defaultProps;

export default SubscriptionPage;
